import React, { KeyboardEventHandler, forwardRef, useCallback } from 'react';

import cx from 'classnames';

import { Badge } from '~shared/components/Badge';
import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import { ariaAttr } from '~shared/utils/dom';
import { normalizeKey } from '~shared/utils/normalize-keycode';
import { toNumber } from '~shared/utils/number';

import { NumericInputSpinButton } from './NumericInputSpinButton';
import { UseNumericInputProps, useNumericInput } from './useNumericInput';
import { getStepFactor, isValidNumericKeyboardEvent } from './utils';

export interface NumericInputProps extends UseNumericInputProps {
  pattern?: string;
  clampValueOnBlur?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  bgColor?: 'grey' | 'white';
  internalLabel?: string;
  showProBadge?: 'focus' | 'always' | 'never';
}

/**
 * @see https://www.w3.org/TR/wai-aria-practices-1.1/#spinbutton
 */
export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  function NumericInput(
    props: NumericInputProps,
    ref: React.RefObject<HTMLInputElement>
  ) {
    const {
      clampValueOnBlur = true,
      textAlign = 'left',
      bgColor = 'grey',
      internalLabel,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultValue,
      showProBadge = 'never',
      allowEmptyString,
      ...rest
    } = props;
    const { min, max, step } = rest;
    const {
      inputRef,
      value,
      valueAsNumber,
      isOutOfRange,
      update,
      increment,
      decrement,
      format,
      getSpinUpButtonProps,
      getSpinDownButtonProps,
    } = useNumericInput(props);
    const validateAndClamp = useCallback(() => {
      let next = value;

      if (next === '') return;

      if (next < min) {
        next = min;
      }

      if (next > max) {
        next = max;
      }

      format(next);
    }, [value, min, max, format]);

    const handleChange = useCallback(
      (event) => {
        if (event.target.value === '') {
          update(allowEmptyString ? '' : 0);
        } else {
          update(event.target.value);
        }
      },
      [update, allowEmptyString]
    );

    const handleBlur = useCallback(() => {
      if (clampValueOnBlur) {
        validateAndClamp();
      }
    }, [clampValueOnBlur, validateAndClamp]);

    const handleKeyDown: KeyboardEventHandler = useCallback(
      (event) => {
        if (!isValidNumericKeyboardEvent(event, inputRef.current.value)) {
          event.preventDefault();
        }

        const stepFactor = getStepFactor(event) * toNumber(step);
        const eventKey = normalizeKey(event);
        const keyMap = {
          ArrowUp: () => increment(stepFactor),
          ArrowDown: () => decrement(stepFactor),
          Home: () => update(min),
          End: () => update(max),
        };
        const action = keyMap[eventKey];

        if (action) {
          event.preventDefault();
          action(event);
        }
      },
      [inputRef, min, max, step, increment, decrement, update]
    );

    const a11yProps = {
      'aria-valuemin': toNumber(min),
      'aria-valuemax': toNumber(max),
      'aria-disabled': rest.disabled,
      'aria-valuenow': Number.isNaN(valueAsNumber) ? undefined : valueAsNumber,
      'aria-invalid': ariaAttr(rest['aria-invalid'] || isOutOfRange),
      // 'aria-valuetext': ariaValueText,
    };

    const containerClass = cx(
      'group relative flex rounded-2 pl-2',
      bgColor === 'grey' && 'bg-grey-50',
      bgColor === 'white' && 'bg-white'
    );
    const inputClass = cx(
      'w-full py-2 pr-1 group-focus-within:pr-1 m-0 font-sans text-base leading-8 text-blue-900 bg-transparent border-0 outline-none appearance-none',
      internalLabel ? 'pr-1' : 'pr-4',
      textAlign === 'left' && 'text-left',
      textAlign === 'center' && 'text-center',
      textAlign === 'right' && 'text-right'
    );
    const badgeWrapperClass = cx(
      showProBadge === 'never' && 'hidden',
      showProBadge === 'focus' && 'hidden group-focus-within:flex'
    );

    return (
      <div className={containerClass} ref={ref}>
        <FocusIndicator notOffset alwaysVisible />
        <label className="flex items-center">
          <div className={badgeWrapperClass}>
            <Badge tone="pro">Pro</Badge>
          </div>
          <input
            {...rest}
            className={inputClass}
            inputMode="decimal"
            type="text"
            pattern="[0-9]*(.[0-9]+)?"
            value={value}
            role="spinbutton"
            autoComplete="off"
            autoCorrect="off"
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            onBlur={handleBlur}
            {...a11yProps}
            ref={inputRef}
          />
          {internalLabel ? (
            <div className="mr-4 group-focus-within:mr-0">
              <span className="text-sans text-base leading-8 text-grey-600 group-focus-within:text-grey-800 select-none">
                {internalLabel}
              </span>
            </div>
          ) : null}
        </label>
        <div className="hidden group-focus-within:flex flex-col flex-none w-8 mr-1">
          <NumericInputSpinButton {...getSpinUpButtonProps()}>
            <NumericInputSpinButton.Up />
          </NumericInputSpinButton>
          <NumericInputSpinButton {...getSpinDownButtonProps()}>
            <NumericInputSpinButton.Down />
          </NumericInputSpinButton>
        </div>
      </div>
    );
  }
);
